import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { MessageBar, Pivot, PivotItem, Stack } from '@fluentui/react';
import { toast } from 'react-toastify';
import { buildStorefrontPatch, IStorefrontSettings } from '../../../../models/Storefront';
import { useGetGlobalStorefrontSettingsQuery, useGetStorefrontQuery, usePatchStorefrontMutation } from '../../../../store/endpoints/StorefrontSettingsEndpoints';
import { ExtensionType } from '../../../../Constants';
import SubExtensionDropdown from '../../../../components/ExtensionFolios/SubExtensionDropdown';
import StorefrontOpportunityExtensionForm from '../../../../components/Storefront/StorefrontOpportunityExtensionForm';
import StorefrontSettingsTitleBar from '../../../../components/Storefront/StorefrontSettingsTitleBar';
import useStorefrontEditLease from '../../../../hooks/useStorefrontEditLease';
import { StorefrontReadOnlyRegion } from '../../../../components/Storefront/StorefrontEditLock';
import { storefrontSaveErrorMessage } from '../../../../logic/storefrontLeaseConflict';
import Setting from '../../../../components/Common/Setting';
import { useUrlTabState } from '../../../../hooks/useUrlState';

interface StorefrontIntegrationsPageParams {
    storefront: string;
}

const StorefrontIntegrationsPage: React.FC<RouteComponentProps<StorefrontIntegrationsPageParams>> = (props) => {
    // In the URL so a documentation link can land on the tab holding the setting it describes.
    const [selectedTab, setSelectedTab] = useUrlTabState('opportunities');
    const { storefront } = props.match.params;
    const storefrontQuery = useGetStorefrontQuery(storefront, { skip: !storefront });
    const globalSettingsQuery = useGetGlobalStorefrontSettingsQuery();
    const [patchStorefront] = usePatchStorefrontMutation();

    const [localSettings, setLocalSettingsRaw] = React.useState<IStorefrontSettings | undefined>();
    const localSettingsRef = React.useRef<IStorefrontSettings | undefined>();
    const originalSettingsRef = React.useRef<IStorefrontSettings | undefined>();
    // Every dropdown here saves on change, so a lease held by an assistant turn would turn each pick
    // into a rejected patch. One flag switches the form off instead.
    const { locked: readOnly } = useStorefrontEditLease(storefront);

    const setLocalSettings = React.useCallback((s: IStorefrontSettings) => {
        localSettingsRef.current = s;
        setLocalSettingsRaw(s);
    }, []);

    React.useEffect(() => {
        if (storefrontQuery.data && !originalSettingsRef.current) {
            setLocalSettings(storefrontQuery.data);
            originalSettingsRef.current = storefrontQuery.data;
        }
    }, [storefrontQuery.data, setLocalSettings]);

    const handleSettingsChanged = React.useCallback(() => {
        originalSettingsRef.current = undefined;
    }, []);

    const doSave = React.useCallback(async (current: IStorefrontSettings) => {
        if (!originalSettingsRef.current) return;
        const patch = buildStorefrontPatch(originalSettingsRef.current, current);
        if (Object.keys(patch).length === 0) return;
        try {
            await patchStorefront({ id: storefront, patch }).unwrap();
            originalSettingsRef.current = { ...originalSettingsRef.current, ...patch };
            toast.success('Settings saved');
        } catch (e) {
            toast.error(storefrontSaveErrorMessage(e, 'Failed to save settings'));
        }
    }, [patchStorefront, storefront]);

    if (storefrontQuery.isLoading || !localSettings) {
        return <div>Loading...</div>;
    }

    const handleFormChange = (updated: IStorefrontSettings) => {
        setLocalSettings(updated);
        doSave(updated);
    };

    const globalSettings = globalSettingsQuery.data;

    return (
        <div>
            <StorefrontSettingsTitleBar
                storefront={storefront}
                title="Integrations"
                onSettingsChanged={handleSettingsChanged}
            />
            <StorefrontReadOnlyRegion readOnly={readOnly}>
            <Pivot selectedKey={selectedTab} onLinkClick={(item) => setSelectedTab(item?.props.itemKey)}>
                <PivotItem headerText='Opportunities' itemKey='opportunities'>
                    <StorefrontOpportunityExtensionForm
                        settingIdPrefix='storefront.integrations.'
                        showQuoteRequestSettings={true}
                        subExtensionFolioCompositeId={globalSettings?.opportunityExtension}
                        globalSalesOrderEnabled={globalSettings?.enableSalesOrderCreation}
                        settings={localSettings}
                        onChange={handleFormChange}
                    />
                </PivotItem>
                <PivotItem headerText='Customers' itemKey='customers'>
                    <Stack tokens={{ childrenGap: '1em' }}>
                        <MessageBar>If set, this extension will be used during customer signup to search your CRM for a related record. Overrides the global setting.</MessageBar>
                        <Setting id='storefront.integrations.customerLookupExtension'>
                            <SubExtensionDropdown
                                type={ExtensionType.StorefrontCustomer}
                                label='Customer Lookup Extension'
                                extension={localSettings.customerLookupExtension}
                                onChange={(extension) => {
                                    const updated = { ...(localSettingsRef.current ?? localSettings), customerLookupExtension: extension };
                                    setLocalSettings(updated);
                                    doSave(updated);
                                }}
                            />
                        </Setting>
                    </Stack>
                </PivotItem>
                <PivotItem headerText='Locations' itemKey='locations'>
                    <Stack tokens={{ childrenGap: '1em' }}>
                        <MessageBar>The location lookup feature allows customers to choose their shipping and billing addresses from a dropdown populated with locations from your CRM. Overrides the global setting.</MessageBar>
                        <Setting id='storefront.integrations.locationLookupExtension'>
                            <SubExtensionDropdown
                                type={ExtensionType.Locations}
                                label='Location Lookup Extension'
                                extension={localSettings.locationLookupExtension}
                                onChange={(extension) => {
                                    const updated = { ...(localSettingsRef.current ?? localSettings), locationLookupExtension: extension };
                                    setLocalSettings(updated);
                                    doSave(updated);
                                }}
                            />
                        </Setting>
                    </Stack>
                </PivotItem>
                <PivotItem headerText='Tax' itemKey='tax'>
                    <Stack tokens={{ childrenGap: '1em' }}>
                        <Setting id='storefront.integrations.taxExtension'>
                            <SubExtensionDropdown
                                type={ExtensionType.Tax}
                                label='Tax Extension'
                                extension={localSettings.taxExtension}
                                onChange={(extension) => {
                                    const updated = { ...(localSettingsRef.current ?? localSettings), taxExtension: extension };
                                    setLocalSettings(updated);
                                    doSave(updated);
                                }}
                            />
                        </Setting>
                    </Stack>
                </PivotItem>
            </Pivot>
            </StorefrontReadOnlyRegion>
        </div>
    );
};

export default StorefrontIntegrationsPage;
